import type { Outbreak, TickerItem } from "#/server/data-fetchers.js";
import { formatDate, type TranslateFn } from "#/lib/i18n.js";

type Props = {
  outbreak: Outbreak;
  ticker: TickerItem[];
  locale: string;
  t: TranslateFn;
};

/**
 * Breaking-news banner. Renders ONLY when:
 *   - the current outbreak has status `emerging`, OR
 *   - any ticker item has severity `urgent`.
 *
 * Sweep animation is a CSS keyframe (no Framer) and is automatically
 * suppressed by `prefers-reduced-motion: reduce` via the stylesheet.
 */
export function BreakingBanner({ outbreak, ticker, locale, t }: Props) {
  const safeTicker = ticker ?? [];
  const hasUrgentTicker = safeTicker.some((item) => item.severity === "urgent");
  const isEmerging = outbreak?.status === "emerging";

  if (!hasUrgentTicker && !isEmerging) {
    return null;
  }

  const lastUpdatedSource = hasUrgentTicker
    ? safeTicker.find((i) => i.severity === "urgent")?.publishedAt ??
      outbreak?.startedAt
    : outbreak?.startedAt;

  return (
    <div
      role="alert"
      aria-label={t("breaking.ariaLabel")}
      className="breaking-banner-sweep"
      style={{
        backgroundColor: "var(--color-accent-emergency)",
        color: "var(--color-accent-emergency-fg)",
        paddingBlock: "0.625rem",
        paddingInline: "1rem",
      }}
    >
      <div
        style={{
          maxWidth: "76rem",
          marginInline: "auto",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "0.75rem",
          fontSize: "0.875rem",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            fontFamily: "var(--font-mono)",
            fontWeight: 700,
            letterSpacing: "0.08em",
            paddingInline: "0.5rem",
            paddingBlock: "0.125rem",
            borderRadius: "0.25rem",
            backgroundColor: "rgba(0,0,0,0.2)",
            fontSize: "0.6875rem",
          }}
        >
          {t("breaking.label")}
        </span>
        <strong style={{ fontWeight: 600 }}>{outbreak.name}</strong>
        <span
          aria-hidden="true"
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.6875rem",
            paddingInline: "0.5rem",
            paddingBlock: "0.125rem",
            borderRadius: "0.25rem",
            border: "1px solid rgba(255,255,255,0.4)",
            letterSpacing: "0.05em",
          }}
        >
          {t("breaking.active")}
        </span>
        <span
          style={{
            marginInlineStart: "auto",
            fontSize: "0.75rem",
            opacity: 0.92,
            fontFamily: "var(--font-mono)",
          }}
        >
          {t("breaking.lastUpdated", {
            time: formatDate(lastUpdatedSource, locale, {
              dateStyle: "short",
              timeStyle: "short",
            }),
          })}
        </span>
      </div>
    </div>
  );
}
